import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Users, Award, ArrowRight, Star, Clock, CheckCircle } from 'lucide-react';

export default function HomePage() {
  useEffect(() => { document.title = 'H.J.Ä.L.P. — Hem'; }, []);
  const features = [
    {
      icon: BookOpen,
      title: 'Evidensbaserade Metoder',
      description: 'Lär dig de senaste och mest effektiva KBT-teknikerna som är vetenskapligt beprövade.'
    },
    {
      icon: Users,
      title: 'Experthandledning',
      description: 'Få vägledning från erfarna KBT-handledare och programledare inom rehabilitering.'
    },
    {
      icon: Award,
      title: 'Certifiering',
      description: 'Erhåll officiella certifikat som bekräftar att du genomfört programmet.'
    },
    {
      icon: Clock,
      title: 'Vägledning till ett bättre liv',
      description: 'Tydlig översiktsanalys av dina framgångar och framsteg i programmet.'
    }
  ];

  const courses = [
    {
      title: 'Individuellt KBT-program',
      description: 'En individuellt anpassad introduktion till kognitiv beteendeterapi och dess tillämpningar i din vardag.',
      duration: '48 sessioner / 4 moduler',
      level: 'Individuellt anpassat',
      rating: 'Fullständigt utvärderingsmaterial och certifikat.',
      students: 'Max 8 personer per grupp'
    },
    {
      title: 'KBT-stöd för ADHD',
      description: 'Specialiserat KBT-stöd för dig med ADHD — konkreta verktyg för struktur, impulshantering och fokus.',
      duration: '16 veckor/1 ggr i veckan',
      level: 'Tillägg för dig med ADHD',
      rating: 'Fullständigt utvärderingsmaterial och certifikat.',
      students: 'Max 4 personer per grupp'
    },
    {
      title: 'KBT i socioekonomisk kontext',
      description: 'Fördjupat arbete kring samhällsproblematik och socioekonomiska faktorer med KBT som verktyg.',
      duration: '16 veckor/1 ggr i veckan',
      level: 'För dig med djupa rötter',
      rating: 'Fullständigt utvärderingsmaterial och certifikat.',
      students: 'Max 4-6 personer per grupp'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-95"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Badge variant="secondary" className="mb-6 bg-white/20 text-white border-white/30">
              🎓 KBT-baserat rehabiliteringsprogram i socioekonomisk kontext
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Välkommen till <span className="text-primary-glow">H.J.Ä.L.P.</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Din portal för KBT-baserat rehabiliteringsstöd och personlig utveckling.
              Bygg upp dig själv och H.J.Ä.L.P. andra att ta nästa steg mot ett bättre liv.
            </p>
            <p className="text-sm text-white/60 mb-4">
              H.J.Ä.L.P. är ett pedagogiskt stödprogram — inte hälso- och sjukvård.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-lg px-8 py-6 bg-white text-primary hover:bg-white/90">
                <Link to="/register">
                  Börja din resa
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="secondary" asChild className="text-lg px-8 py-6">
                <Link to="#courses">
                  Utforska kurser
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Varför välja H.J.Ä.L.P.?
            </h2>
            <p className="text-xl text-muted-foreground">
              Vi erbjuder det mest omfattande, professionella och individuella KBT-programmet,
              med fokus på praktisk tillämpning och evidensbaserade metoder.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center shadow-elegant hover:shadow-glow transition-all duration-300 bg-gradient-card border-0">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                    <feature.icon className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Courses Section */}
      <section id="courses" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Våra fullständiga kurser
            </h2>
            <p className="text-xl text-muted-foreground">
              Upptäck våra mest efterfrågade kurser inom KBT och börja din personliga utveckling idag.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <Card key={index} className="shadow-elegant hover:shadow-glow transition-all duration-300 bg-gradient-card border-0 group">
                <CardHeader>
                  <div className="flex justify-between items-start mb-4">
                    <Badge variant="secondary">{course.level}</Badge>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Star className="h-4 w-4 text-yellow-500 mr-1" />
                      {course.rating}
                    </div>
                  </div>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    {course.title}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {course.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-4 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {course.duration}
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {course.students} studenter
                    </div>
                  </div>
                  <Button className="w-full" asChild>
                    <Link to="/register">
                      Börja kurs
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Redo att börja din H.J.Ä.L.P.-resa?
            </h2>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Gå med och träffa dem som redan tagit sig ut och utvecklat sina färdigheter med hjälp
              av H.J.Ä.L.P.-programmets kognitiv beteendeterapi, tillsammans hjälps vi åt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-lg px-8 py-6 bg-white text-primary hover:bg-white/90">
                <Link to="/register">
                  Ansök till H.J.Ä.L.P.
                  <CheckCircle className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="secondary" asChild className="text-lg px-8 py-6">
                <Link to="/contact">
                  Kontakta oss
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}